import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSupportCaseList } from '../hooks/useSupportCases';
import { SupportCaseStatusBadge } from '../components/SupportCaseStatusBadge';
import { SupportCaseDetailPanel } from '../components/SupportCaseDetailPanel';
import type { SupportCaseStatus } from '../types/supportCase';

type FilterMode = 'NON_FINAL' | 'RESOLVED' | 'ABANDONED';

const FILTER_STATUSES: Record<FilterMode, readonly SupportCaseStatus[]> = {
  NON_FINAL: ['OPEN', 'IN_PROGRESS'],
  RESOLVED: ['RESOLVED'],
  ABANDONED: ['ABANDONED'],
};

const SupportCases = () => {
  const { t } = useTranslation(['pages', 'errors']);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCaseId = searchParams.get('caseId') ?? '';

  const filterParam = (searchParams.get('filter') ?? 'NON_FINAL') as FilterMode;
  const filter: FilterMode = ['NON_FINAL', 'RESOLVED', 'ABANDONED'].includes(filterParam)
    ? filterParam
    : 'NON_FINAL';

  const { data, isLoading, isError } = useSupportCaseList({
    statuses: FILTER_STATUSES[filter],
    sort: 'openedAt,desc',
  });

  const cases = data?.content ?? [];

  function setFilter(mode: FilterMode) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('filter', mode);
      next.delete('caseId');
      return next;
    });
  }

  function selectCase(caseId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('caseId', caseId);
      return next;
    });
  }

  function closePanel() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('caseId');
      return next;
    });
  }

  const filterButtons: Array<{ mode: FilterMode; label: string }> = [
    { mode: 'NON_FINAL', label: t('pages:filterAll') },
    { mode: 'RESOLVED', label: t('pages:filterResolved') },
    { mode: 'ABANDONED', label: t('pages:filterAbandoned') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('pages:supportCasesHeading')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('pages:supportCasesSubHeading')}</p>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2">
        {filterButtons.map(({ mode, label }) => (
          <button
            key={mode}
            onClick={() => setFilter(mode)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              filter === mode
                ? 'bg-primary-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-gray-500 text-sm">{t('pages:loadingCaseDetails')}</div>
      )}

      {isError && (
        <div className="text-red-600 text-sm">{t('errors:failedToLoadSupportCases')}</div>
      )}

      {!isLoading && !isError && cases.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {t('pages:noSupportCases')}
        </div>
      )}

      {!isLoading && !isError && cases.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {cases.map((sc) => (
            <button
              key={sc.caseId}
              onClick={() => selectCase(sc.caseId)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                selectedCaseId === sc.caseId ? 'bg-primary-50' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <SupportCaseStatusBadge status={sc.status} />
                  <span className="text-sm font-medium text-gray-900 truncate font-mono">
                    {sc.caseId}
                  </span>
                  <span className="text-sm text-gray-500 truncate hidden sm:block font-mono">
                    {sc.bookingId}
                  </span>
                </div>
                <span className="text-xs text-gray-400 shrink-0">
                  {new Date(sc.openedAt).toLocaleDateString()}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail panel */}
      {selectedCaseId && (
        <SupportCaseDetailPanel
          caseId={selectedCaseId}
          onClose={closePanel}
        />
      )}
    </div>
  );
};

export default SupportCases;
